import React from 'react';

const HistorySection = () => {
  return (
    <section id="histoire" className="py-32 md:py-40 bg-[#faf9f7]">
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        
        {/* Section Header */}
        <div className="text-center mb-24 md:mb-32">
          <p 
            className="text-sm uppercase tracking-[0.3em] text-gray-500 mb-6"
            style={{ fontFamily: "var(--font-body)" }}
          >
            Notre héritage
          </p>
          <h2 
            className="text-4xl md:text-5xl lg:text-6xl font-light mb-8 text-gray-900 tracking-[0.15em] uppercase"
            style={{ fontFamily: "var(--font-display)" }}
          >
            L'Histoire de La Rotonde
          </h2>
          <div className="w-16 h-px bg-gray-400 mx-auto"></div>
        </div>

        {/* Timeline Content */}
        <div className="max-w-6xl mx-auto">
          
          {/* ==================== 1891 ==================== */}
          <div className="mb-32 md:mb-40">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Text Content */}
              <div className="order-2 lg:order-1">
                <div className="max-w-lg">
                  {/* Year */}
                  <span 
                    className="block text-6xl md:text-7xl lg:text-8xl font-light text-gray-200 mb-6 leading-none tracking-[0.1em]"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    1891
                  </span>
                  
                  {/* Title */}
                  <h3 
                    className="text-2xl md:text-3xl font-light mb-6 text-gray-900 tracking-[0.15em] uppercase"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    La naissance
                  </h3>
                  
                  {/* Decorative line */}
                  <div className="w-12 h-px bg-gray-400 mb-8"></div>
                  
                  {/* Description */}
                  <p 
                    className="text-gray-700 leading-loose text-lg md:text-xl font-light"
                    style={{ fontFamily: "var(--font-body)", lineHeight: '2' }}
                  >
                    L'histoire de La Rotonde débute en 1891, à l'époque où Divonne-les-Bains 
                    s'impose comme une station thermale et hôtelière de renom. Construite attenante 
                    au Grand Hôtel, aujourd'hui Casino, La Rotonde accueillait alors un café-billard, 
                    lieu de détente et de rencontres pour les curistes de passage.
                  </p>
                </div>
              </div>
              
              {/* Image */}
              <div className="order-1 lg:order-2">
                <figure className="relative">
                  <div className="overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_divonne-dining/artifacts/evp2f0c8_res.png"
                      alt="La Rotonde en 1891 - Vue historique du bâtiment"
                      className="w-full h-auto grayscale hover:grayscale-0 transition-all duration-700"
                      style={{ filter: 'sepia(15%)' }}
                    />
                  </div>
                  <figcaption 
                    className="mt-4 text-sm text-gray-500 italic tracking-wide"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    Archives — La Rotonde attenante au Grand Hôtel, circa 1891
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>

          {/* Elegant divider */}
          <div className="flex items-center justify-center mb-32 md:mb-40">
            <div className="w-24 h-px bg-gray-300"></div>
            <div className="mx-8 text-gray-300 text-xl">✦</div>
            <div className="w-24 h-px bg-gray-300"></div>
          </div>

          {/* ==================== 1932 ==================== */}
          <div className="mb-32 md:mb-40">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Image */}
              <div>
                <figure className="relative">
                  <div className="overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_divonne-dining/artifacts/nsriigs6_ChatGPT%20Image%2024%20f%C3%A9vr.%202026%2C%2010_44_33.png"
                      alt="La Rotonde avant et après la reconstruction de 1932"
                      className="w-full h-auto"
                      style={{ filter: 'sepia(10%)' }}
                    />
                  </div>
                  <figcaption 
                    className="mt-4 text-sm text-gray-500 italic tracking-wide"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    Reconstruction du bâtiment — Hôtel Bon Accueil, 1932
                  </figcaption>
                </figure>
              </div>
              
              {/* Text Content */}
              <div>
                <div className="max-w-lg lg:ml-auto">
                  {/* Year */}
                  <span 
                    className="block text-6xl md:text-7xl lg:text-8xl font-light text-gray-200 mb-6 leading-none tracking-[0.1em]"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    1932
                  </span>
                  
                  {/* Title */}
                  <h3 
                    className="text-2xl md:text-3xl font-light mb-6 text-gray-900 tracking-[0.15em] uppercase"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    La reconstruction
                  </h3>
                  
                  {/* Decorative line */}
                  <div className="w-12 h-px bg-gray-400 mb-8"></div>
                  
                  {/* Description */}
                  <p 
                    className="text-gray-700 leading-loose text-lg md:text-xl font-light"
                    style={{ fontFamily: "var(--font-body)", lineHeight: '2' }}
                  >
                    En 1932, le bâtiment est démonté puis reconstruit à l'identique pour devenir le 
                    salon et la salle à manger de l'hôtel Bon Accueil. Pendant plusieurs décennies, 
                    il accompagne l'essor de Divonne et accueille les visiteurs qui contribuent à la 
                    renommée de la station.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Elegant divider */}
          <div className="flex items-center justify-center mb-32 md:mb-40">
            <div className="w-24 h-px bg-gray-300"></div>
            <div className="mx-8 text-gray-300 text-xl">✦</div>
            <div className="w-24 h-px bg-gray-300"></div>
          </div>

          {/* ==================== Aujourd'hui ==================== */}
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Text Content */}
              <div className="order-2 lg:order-1">
                <div className="max-w-lg">
                  {/* Year */}
                  <span 
                    className="block text-5xl md:text-6xl lg:text-7xl font-light text-gray-200 mb-6 leading-none tracking-[0.1em] uppercase"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Aujourd'hui
                  </span>
                  
                  {/* Title */}
                  <h3 
                    className="text-2xl md:text-3xl font-light mb-6 text-gray-900 tracking-[0.15em] uppercase"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Une nouvelle page
                  </h3>
                  
                  {/* Decorative line */}
                  <div className="w-12 h-px bg-gray-400 mb-8"></div>
                  
                  {/* Description */}
                  <p 
                    className="text-gray-700 leading-loose text-lg md:text-xl font-light"
                    style={{ fontFamily: "var(--font-body)", lineHeight: '2' }}
                  >
                    Aujourd'hui, La Rotonde entre dans une nouvelle phase de son histoire. Le lieu 
                    évolue, la gouvernance change, mais sa vocation reste intacte : accueillir, 
                    rassembler et faire vivre des moments autour de la table.
                  </p>
                </div>
              </div>
              
              {/* Image */}
              <div className="order-1 lg:order-2">
                <figure className="relative">
                  <div className="overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_divonne-dining/artifacts/slp97evy_Web-25.jpg"
                      alt="La Rotonde aujourd'hui - Intérieur du restaurant"
                      className="w-full h-auto"
                    />
                  </div>
                  <figcaption 
                    className="mt-4 text-sm text-gray-500 italic tracking-wide"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    La Rotonde — Divonne-les-Bains, 2025
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HistorySection;
